import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  TENANT_BOOTSTRAP_ERROR_CODES,
  tenantBootstrapRuntime,
} from "../services/tenantBootstrap";

const TenantBootstrapContext = createContext(null);

const getUserFacingError = (error) => {
  switch (error?.code) {
    case TENANT_BOOTSTRAP_ERROR_CODES.INVALID_TENANT_SLUG:
      return "Invalid tenant URL";
    case TENANT_BOOTSTRAP_ERROR_CODES.TENANT_NOT_FOUND:
      return "Tenant not found";
    case TENANT_BOOTSTRAP_ERROR_CODES.TENANT_INACTIVE:
      return "Tenant unavailable";
    case TENANT_BOOTSTRAP_ERROR_CODES.TENANT_CONFIG_INVALID:
      return "Tenant configuration error";
    case TENANT_BOOTSTRAP_ERROR_CODES.CENTRAL_FIRESTORE_UNAVAILABLE:
      return "Unable to resolve tenant right now";
    case TENANT_BOOTSTRAP_ERROR_CODES.CUSTOMER_FIREBASE_INIT_FAILED:
      return "Unable to connect to customer workspace";
    default:
      return "Unable to start OceanLogix";
  }
};

export const TenantBootstrapProvider = ({ children }) => {
  const location = useLocation();
  const [state, setState] = useState({
    status: "loading",
    phase: "resolving",
    tenantBootstrap: null,
    source: null,
    error: null,
  });

  useEffect(() => {
    let disposed = false;

    setState({
      status: "loading",
      phase: "resolving",
      tenantBootstrap: null,
      source: null,
      error: null,
    });

    tenantBootstrapRuntime
      .bootstrapForPathname(location.pathname)
      .then(({ tenantBootstrap, source }) => {
        if (disposed) return;
        setState({
          status: "ready",
          phase: "ready",
          tenantBootstrap,
          source,
          error: null,
        });
      })
      .catch((error) => {
        if (disposed) return;
        console.error("[Tenant bootstrap] failed", {
          code: error?.code,
          message: error?.message,
          details: error?.details,
        });
        setState({
          status: "error",
          phase: "error",
          tenantBootstrap: null,
          source: null,
          error,
        });
      });

    return () => {
      disposed = true;
    };
  }, [location.pathname]);

  const value = useMemo(
    () => ({
      ...state,
      errorMessage: getUserFacingError(state.error),
    }),
    [state]
  );

  return (
    <TenantBootstrapContext.Provider value={value}>
      {children}
    </TenantBootstrapContext.Provider>
  );
};

export const useTenantBootstrap = () => {
  const context = useContext(TenantBootstrapContext);
  if (!context) {
    throw new Error(
      "useTenantBootstrap must be used within TenantBootstrapProvider"
    );
  }

  return context;
};
